<template>
  <div class="approvals-assign-approvers px-6 pt-6 pb-6">
    <ApprovalsPanelBackButton
      class="mb-4"
      @click="handleBack"
    />

    <div class="text-h1 text-textDarkest mb-4">
      Assign Approvers
    </div>

    <p class="text-body-1 text-textDark mb-6">
      Assign approvers to the
      <span class="font-weight-bold text-textDark">{{ selectedTemplate?.name }}</span>
      workflow for
      <span class="font-weight-bold text-textDark">{{ jobProfileTitle }}</span>.
    </p>

    <div
      v-for="stage in assignmentStages"
      :key="stage.stageNumber"
      class="approvals-assign-approvers-stage mb-6"
    >
      <div class="text-h2 text-textDarkest mb-2">
        Stage {{ stage.stageNumber }}: {{ stage.stageName }}
      </div>

      <div
        v-for="approvalSlot in stage.approvalSlots"
        :key="approvalSlot.approvalSlotIndex"
        class="approvals-assign-approvers-slot mb-4"
      >
        <div class="d-flex align-center mb-1">
          <span class="text-body-1 text-textDarkest font-weight-medium">{{ approvalSlot.name }}</span>
          <DpplDarkTooltip
            v-if="approvalSlot.description"
            light-mode
            tooltip-placement="top"
            class="approvals-assign-approvers-info-tooltip"
          >
            <template #activator="{ onMouseenter, onMouseleave }">
              <span
                class="d-inline-flex"
                @mouseenter="onMouseenter"
                @mouseleave="onMouseleave"
              >
                <v-icon
                  icon="$information-outline"
                  size="16"
                  color="iconDark"
                  class="ml-1"
                />
              </span>
            </template>
            <template #tooltip-content>
              <div class="text-body-2 text-textDark">
                {{ approvalSlot.description }}
              </div>
            </template>
          </DpplDarkTooltip>
        </div>

        <ApprovalApproverSearch
          :model-value="approvalSlot.selectedUsers"
          :exclude-ids="approvalSlot.selectedUsers.map((selectedUser) => selectedUser.id)"
          @update:model-value="handleApprovalSlotUpdate(approvalSlot.approvalSlotIndex, $event)"
        />
      </div>
    </div>

    <v-btn
      block
      rounded="lg"
      color="primary"
      variant="flat"
      height="40"
      class="text-button-1"
      :disabled="!canSubmitCreateFlow || isCreating"
      :loading="isCreating"
      @click="emit('submit')"
    >
      Start Approval Flow
    </v-btn>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import ApprovalApproverSearch from '@/components/jobProfiles/editor/ApprovalApproverSearch.vue';
import ApprovalsPanelBackButton from '@/components/jobProfiles/editor/approvals/ApprovalsPanelBackButton.vue';
import DpplDarkTooltip from '@/components/datapeople/DpplDarkTooltip.vue';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useApprovalFlows } from '@/stores/jobProfiles/approvalFlows';

const emit = defineEmits(['submit']);

const { jobProfile } = storeToRefs(useCurrentJobProfile());

const approvalFlowsStore = useApprovalFlows();
const {
  selectedTemplate,
  slotAssignments,
  canSubmitCreateFlow,
  isCreating,
} = storeToRefs(approvalFlowsStore);
const { backToTemplateSelect, updateApprovalSlotApprovers } = approvalFlowsStore;

const jobProfileTitle = computed(() => jobProfile.value?.title ?? 'job profile');

const assignmentStages = computed(() => {
  const stages = [];

  slotAssignments.value.forEach((assignment, approvalSlotIndex) => {
    const lastStage = stages[stages.length - 1];

    if (!lastStage || lastStage.stageNumber !== assignment.stageNumber) {
      stages.push({
        stageNumber: assignment.stageNumber,
        stageName: assignment.stageName,
        approvalSlots: [],
      });
    }

    stages[stages.length - 1].approvalSlots.push({
      ...assignment,
      approvalSlotIndex,
    });
  });

  return stages;
});

const handleBack = () => {
  backToTemplateSelect();
};

const handleApprovalSlotUpdate = (approvalSlotIndex, selectedUsers) => {
  updateApprovalSlotApprovers(approvalSlotIndex, selectedUsers);
};
</script>

<style scoped lang="less">
.approvals-assign-approvers-info-tooltip {
  :deep(.activator) {
    display: flex;
    align-items: center;
  }
}
</style>
